'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { format } from 'date-fns';
import { ChevronUp, ChevronDown, X } from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { cn } from '../../lib/utils';

// Truncate wallet address for display
const truncateAddress = (address) => {
  if (!address) return null;
  // Only truncate if it looks like a wallet address
  if (address.startsWith('0x') && address.length >= 10) {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }
  return null;
};

// Get blockchain name from mint info (check both mintInformation and top-level)
const getBlockchainName = (comment) => {
  if (comment?.mintInformation?.chainName) {
    return comment.mintInformation.chainName;
  }
  // Fallback to top-level chainName if available
  if (comment?.chainName) {
    return comment.chainName;
  }
  return null;
};

/**
 * Comment Position Tracker - Discourse-style position indicator
 * Shows current position in comments with jump functionality
 * This is a sticky sidebar component that shares layout space with comments
 */
export function CommentPositionTracker({
  comments = [],
  discussionDate,
  className,
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mobileListOpen, setMobileListOpen] = useState(false);
  const observersRef = useRef([]);
  const isScrollingRef = useRef(false);

  // Filter to top-level comments only
  const topLevelComments = comments.filter((c) => !c.parentId);
  const totalComments = topLevelComments.length;

  // Setup intersection observers for each comment
  useEffect(() => {
    // Clean up existing observers
    observersRef.current.forEach((observer) => observer.disconnect());
    observersRef.current = [];

    if (topLevelComments.length === 0) {
      return;
    }

    // Observer for current position tracking
    const positionObserver = new IntersectionObserver(
      (entries) => {
        // Skip updates while programmatically scrolling
        if (isScrollingRef.current) return;

        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const oid = entry.target.dataset.commentOid;
            const index = topLevelComments.findIndex((c) => c.oid === oid);
            if (index !== -1) {
              setCurrentIndex(index);
            }
          }
        });
      },
      {
        rootMargin: '-10% 0px -70% 0px',
        threshold: 0.1,
      }
    );

    // Observe all comment elements
    topLevelComments.forEach((comment) => {
      const element = document.querySelector(
        `[data-comment-oid="${comment.oid}"]`
      );
      if (element) {
        positionObserver.observe(element);
      }
    });

    observersRef.current = [positionObserver];

    return () => {
      positionObserver.disconnect();
    };
  }, [comments, topLevelComments]);

  // Jump to specific comment with scroll lock
  const jumpToComment = useCallback(
    (index) => {
      const comment = topLevelComments[index];
      if (comment) {
        const element = document.querySelector(
          `[data-comment-oid="${comment.oid}"]`
        );
        if (element) {
          // Lock scroll tracking temporarily
          isScrollingRef.current = true;
          setCurrentIndex(index);

          // Close mobile list if open
          setMobileListOpen(false);

          // Scroll with offset for better visibility (account for mobile bottom bar)
          const isMobile = window.innerWidth < 1024;
          const yOffset = isMobile ? -100 : -80;
          const y =
            element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });

          // Unlock after scroll completes
          setTimeout(() => {
            isScrollingRef.current = false;
          }, 800);
        }
      }
    },
    [topLevelComments]
  );

  // Navigate to next/previous comment - always jump even if same index (re-center)
  const goToNext = useCallback(() => {
    const nextIndex = Math.min(currentIndex + 1, totalComments - 1);
    jumpToComment(nextIndex);
  }, [currentIndex, totalComments, jumpToComment]);

  const goToPrevious = useCallback(() => {
    const prevIndex = Math.max(currentIndex - 1, 0);
    jumpToComment(prevIndex);
  }, [currentIndex, jumpToComment]);

  // Get formatted date/time for a comment
  const getCommentDateTime = useCallback((comment) => {
    if (comment?.createdAt) {
      return format(new Date(comment.createdAt), 'MMM d, h:mm a');
    }
    return '';
  }, []);

  // Get short date for list
  const getCommentDate = useCallback((comment) => {
    if (comment?.createdAt) {
      return format(new Date(comment.createdAt), 'MMM d');
    }
    return '';
  }, []);

  // Get full date for current comment
  const getCurrentDateTime = useCallback(() => {
    const comment = topLevelComments[currentIndex];
    if (comment?.createdAt) {
      return format(new Date(comment.createdAt), 'MMM d, yyyy h:mm a');
    }
    return discussionDate
      ? format(new Date(discussionDate), 'MMM d, yyyy')
      : '';
  }, [topLevelComments, currentIndex, discussionDate]);

  if (totalComments === 0) return null;

  return (
    <>
      {/* Desktop Sidebar - Sticky on right side of comments */}
      <div
        className={cn(
          'hidden lg:block sticky top-24 self-start ml-4 w-52 flex-shrink-0',
          className
        )}
      >
        <div className="bg-zinc-900/80 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden">
          {/* Header with position */}
          <div className="p-3 border-b border-white/10 bg-zinc-800/50">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-lg font-bold text-white">
                  {currentIndex + 1} / {totalComments}
                </div>
                <div className="text-xs text-zinc-500">
                  {getCurrentDateTime()}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goToPrevious}
                  disabled={currentIndex === 0}
                  className="h-6 w-6 text-zinc-400 hover:text-white hover:bg-white/10 disabled:opacity-30"
                >
                  <ChevronUp className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goToNext}
                  disabled={currentIndex >= totalComments - 1}
                  className="h-6 w-6 text-zinc-400 hover:text-white hover:bg-white/10 disabled:opacity-30"
                >
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-2 h-1 bg-zinc-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-violet-500 to-cyan-500 rounded-full transition-all duration-300"
                style={{
                  width: `${((currentIndex + 1) / totalComments) * 100}%`,
                }}
              />
            </div>
          </div>

          {/* Scrollable comment list */}
          <ScrollArea className="h-64">
            <div className="p-1">
              {topLevelComments.map((comment, index) => (
                <button
                  key={comment.oid}
                  onClick={() => jumpToComment(index)}
                  className={cn(
                    'w-full px-3 py-2 text-left rounded-lg transition-all duration-200 group',
                    currentIndex === index
                      ? 'bg-violet-500/20 border-l-2 border-violet-500'
                      : 'hover:bg-white/5 border-l-2 border-transparent'
                  )}
                >
                  <div className="flex items-center justify-between gap-1">
                    {/* Wallet address - comment.creator is the wallet address */}
                    {truncateAddress(comment.creator) ? (
                      <span
                        className={cn(
                          'text-xs font-mono truncate',
                          currentIndex === index
                            ? 'text-violet-300'
                            : 'text-zinc-400 group-hover:text-zinc-200'
                        )}
                      >
                        {truncateAddress(comment.creator)}
                      </span>
                    ) : (
                      <span
                        className={cn(
                          'text-xs text-zinc-500 italic',
                          currentIndex === index ? 'text-zinc-400' : ''
                        )}
                      >
                        Anonymous
                      </span>
                    )}
                    {getBlockchainName(comment) && (
                      <span className="text-[10px] text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded flex-shrink-0">
                        {getBlockchainName(comment)}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-zinc-600 mt-0.5">
                    {getCommentDateTime(comment)}
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* Mobile Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden">
        {/* Mobile Comment List Popup */}
        {mobileListOpen && (
          <div className="absolute bottom-full left-0 right-0 mb-0 mx-2 bg-zinc-900/98 backdrop-blur-md border border-white/10 rounded-t-xl shadow-2xl max-h-[60vh] overflow-hidden">
            {/* Popup Header */}
            <div className="flex items-center justify-between p-3 border-b border-white/10 bg-zinc-800/50">
              <span className="text-sm font-medium text-white">
                Jump to Comment
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileListOpen(false)}
                className="h-6 w-6 text-zinc-400 hover:text-white"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            {/* Comment List */}
            <ScrollArea className="h-64">
              <div className="p-2">
                {topLevelComments.map((comment, index) => (
                  <button
                    key={comment.oid}
                    onClick={() => jumpToComment(index)}
                    className={cn(
                      'w-full px-3 py-3 text-left rounded-lg transition-all duration-200 mb-1',
                      currentIndex === index
                        ? 'bg-violet-500/20 border-l-2 border-violet-500'
                        : 'hover:bg-white/5 border-l-2 border-transparent active:bg-white/10'
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      {/* Wallet address */}
                      {truncateAddress(comment.creator) ? (
                        <span
                          className={cn(
                            'text-sm font-mono',
                            currentIndex === index
                              ? 'text-violet-300'
                              : 'text-zinc-300'
                          )}
                        >
                          {truncateAddress(comment.creator)}
                        </span>
                      ) : (
                        <span className="text-sm text-zinc-500 italic">
                          Anonymous
                        </span>
                      )}
                      {getBlockchainName(comment) && (
                        <span className="text-[10px] text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded">
                          {getBlockchainName(comment)}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                      {getCommentDateTime(comment)}
                    </div>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Bottom Bar */}
        <div className="bg-zinc-900/95 backdrop-blur-sm border-t border-white/10 px-4 py-3 flex items-center justify-between">
          {/* Position indicator */}
          <div className="flex flex-col">
            <span className="text-sm font-medium text-white">
              {currentIndex + 1} / {totalComments}
            </span>
            <span className="text-[10px] text-zinc-500">
              {getCurrentDateTime()}
            </span>
          </div>

          {/* Progress bar - clickable to open list */}
          <button
            onClick={() => setMobileListOpen(!mobileListOpen)}
            className="flex-1 mx-4 h-6 flex items-center justify-center rounded-full bg-zinc-800 hover:bg-zinc-700 active:bg-zinc-600 transition-colors"
          >
            <div className="w-full h-1 bg-zinc-700 rounded-full overflow-hidden mx-2">
              <div
                className="h-full bg-gradient-to-r from-violet-500 to-cyan-500 rounded-full transition-all duration-300"
                style={{
                  width: `${((currentIndex + 1) / totalComments) * 100}%`,
                }}
              />
            </div>
          </button>

          {/* Navigation buttons */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={goToPrevious}
              className="h-8 w-8 p-0 text-zinc-400 hover:text-white active:bg-white/20"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={goToNext}
              className="h-8 w-8 p-0 text-zinc-400 hover:text-white active:bg-white/20"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export default CommentPositionTracker;
